import styles from "./UsageMeter.module.css";

/**
 * UsageMeter — barra de uso tokenizada e acessível (design system Nexus).
 *
 * Consome o shape de `GET /api/tenants/me/plan-usage` (`usage.<recurso>`):
 * `{ used, limit, percentage }`. Quando `limit` é `null` o recurso é ILIMITADO
 * (percentage também vem `null`) — nesse caso não há barra: exibimos só o rótulo
 * "Ilimitado".
 *
 * Distinção importante: `null` (ilimitado, explícito) NÃO é o mesmo que
 * `undefined` (dado ausente — recurso ainda não carregado ou não retornado pelo
 * backend). Se `limit` (ou `used`) vier `undefined`, exibimos um estado neutro
 * "Sem dado" ("—"), sem barra e sem afirmar "Ilimitado".
 *
 * Cor da barra por faixa de uso (tokens semânticos, nunca hex solto):
 *   • ≤ 75%  → --nx-success   (saudável)
 *   • > 75%  → --nx-warning   (atenção)
 *   • > 90%  → --nx-danger    (quase no limite)
 *
 * Acessibilidade: `role="progressbar"` + `aria-valuenow/min/max` + `aria-label`.
 *
 * @param {object}  props
 * @param {string}  props.label       Nome do recurso (ex.: "Membros").
 * @param {number}  props.used        Quantidade usada no ciclo.
 * @param {number|null|undefined} props.limit   Limite do plano; `null` =
 *   ilimitado, `undefined` = dado ausente (estado neutro).
 * @param {number|null} [props.percentage] Percentual pré-calculado pelo backend.
 */
export default function UsageMeter({ label, used, limit, percentage }) {
  // Dado ausente ≠ ilimitado: `undefined` é falta de informação; `null` é a
  // afirmação explícita de que o recurso não tem teto.
  const isNoData = limit === undefined || used === undefined;
  const isUnlimited = !isNoData && limit === null;
  const usedNum = Number(used ?? 0);

  // Preferimos o percentage do backend; caímos no cálculo local só se ausente.
  const rawPct =
    isNoData || isUnlimited
      ? 0
      : typeof percentage === "number"
        ? percentage
        : !(limit > 0)
          ? 0
          : (usedNum / limit) * 100;
  const pct = Math.min(Math.max(Math.round(rawPct), 0), 100);

  const level = pct > 90 ? "danger" : pct > 75 ? "warning" : "success";

  return (
    <div className={styles.meter}>
      <div className={styles.head}>
        <span className={styles.label}>{label}</span>
        <span className={styles.value}>
          {isNoData ? (
            <span className={styles.noData}>—</span>
          ) : isUnlimited ? (
            <>
              {usedNum} <span className={styles.unlimited}>· ilimitado</span>
            </>
          ) : (
            `${usedNum} / ${limit}`
          )}
        </span>
      </div>

      {isNoData ? (
        <div className={styles.noDataTrack}>
          <span className={styles.noDataTag}>Sem dado</span>
        </div>
      ) : isUnlimited ? (
        <div className={styles.unlimitedTrack}>
          <span className={styles.unlimitedTag}>Ilimitado</span>
        </div>
      ) : (
        <div
          className={styles.track}
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuetext={`${usedNum} de ${limit}`}
          aria-label={label}
        >
          <div
            className={`${styles.fill} ${styles[level]}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}
